import { Component } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import axios from 'axios';
import { RevealDirective } from '../../core/directives/reveal.directive';
import { MailService } from '../../services';
import { ReferralPayloadModel, MailPayload } from '../../shared/models';

@Component({
  selector: 'hx-referral-form',
  standalone: true,
  imports: [MatButtonModule, MatSnackBarModule, RevealDirective],
  template: `
    <section id="referral-form" class="section-tight">
      <div class="wrap">
        <div class="referral-form reveal" appReveal>
          <span class="case-tag">Refer someone</span>
          <h3 style="font-size:26px">Send us the intro.</h3>
          <p style="max-width:480px">
            Fill this in and we'll take it from here — you'll hear back once we've made contact.
          </p>

          <form (submit)="handleSubmit($event)">
            <div class="form-grid">
              <div class="field">
                <label for="referrerName">Your name</label>
                <input
                  id="referrerName"
                  type="text"
                  [value]="formData.referrerName"
                  (input)="onInputChange($event)"
                  required
                />
              </div>

              <div class="field">
                <label for="referrerEmail">Your email</label>
                <input
                  id="referrerEmail"
                  type="email"
                  [value]="formData.referrerEmail"
                  (input)="onInputChange($event)"
                  required
                />
              </div>

              <div class="field">
                <label for="referredBusiness">Business you're referring</label>
                <input
                  id="referredBusiness"
                  type="text"
                  [value]="formData.referredBusiness"
                  (input)="onInputChange($event)"
                  required
                />
              </div>

              <div class="field">
                <label for="referredEmail">Their contact email</label>
                <input
                  id="referredEmail"
                  type="email"
                  [value]="formData.referredEmail"
                  (input)="onInputChange($event)"
                  required
                />
              </div>

              <div class="field full">
                <label for="projectDetails">
                  What are they looking to build? (optional)
                </label>
                <textarea
                  id="projectDetails"
                  [value]="formData.projectDetails"
                  (input)="onInputChange($event)"
                ></textarea>
              </div>
            </div>

            <div class="hero-actions" style="margin-top:26px">
              <button
                type="submit"
                mat-flat-button
                class="btn btn-accent"
                [disabled]="isLoading"
              >
                {{ isLoading ? 'Sending…' : 'Send referral' }}
              </button>
            </div>

            <p class="form-note">
              Submissions are sent straight to info&#64;hexacorp.xyz.
            </p>
          </form>
        </div>
      </div>
    </section>
  `,
})
export class ReferralFormComponent {
  isLoading = false;

  formData: ReferralPayloadModel = {
    referrerName: '',
    referrerEmail: '',
    referredBusiness: '',
    referredEmail: '',
    projectDetails: '',
  };

  constructor(
    private mailService: MailService,
    private snackBar: MatSnackBar
  ) {}

  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    const { id: field, value } = target;

    if (field in this.formData) {
      this.formData[field as keyof ReferralPayloadModel] = value;
    }
  }

  private showSnackBar(message: string, isSuccess: boolean): void {
    this.snackBar.open(message, 'Close', {
      duration: 3500,
      horizontalPosition: 'left',
      verticalPosition: 'bottom',
      panelClass: [isSuccess ? 'success-snackbar' : 'error-snackbar'],
    });
  }

  handleSubmit(event: Event): void {
    event.preventDefault();

    if (this.isLoading) {
      return;
    }

    this.isLoading = true;

    const payload: MailPayload = {
      subject: 'New Hexacorp referral submission',
      name: this.formData.referrerName,
      from: this.formData.referrerEmail,
      mobile: this.formData.referredBusiness,
      message:
        `Referred business: ${this.formData.referredBusiness}\n` +
        `Referred contact email: ${this.formData.referredEmail}\n\n` +
        `Project details:\n${this.formData.projectDetails || '(none provided)'}`,
    };

    console.log('Payload:', payload);

    this.mailService
      .sendMail(payload)
      .then((response) => {
        console.log('Response:', response);

        if (response.status === 200 || response.status === 201) {
          this.showSnackBar(
            'Your referral has been submitted! Our team will be in touch soon.',
            true
          );

          this.formData = {
            referrerName: '',
            referrerEmail: '',
            referredBusiness: '',
            referredEmail: '',
            projectDetails: '',
          };
        } else {
          console.error('Unexpected response status:', response.status);

          this.showSnackBar(
            'Unexpected server response. Please try again.',
            false
          );
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error)) {
          console.error('Error sending email:', error.response?.data);
          console.error('Status Code:', error.response?.status);

          this.showSnackBar(
            error.response?.data?.message ??
              'There was an error sending your referral. Please try again later.',
            false
          );
        } else {
          console.error('Unexpected error:', error);

          this.showSnackBar(
            'An unexpected error occurred. Please try again later.',
            false
          );
        }
      })
      .finally(() => {
        // Always re-enable the button after the request finishes.
        this.isLoading = false;
      });
  }
}